<script lang="ts">
import {z} from 'zod';

export default {
  name: 'EditDisplayOptionsModal',
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const DisplayOptionsSchema = z.object({
  carrier: z.string().trim(),
  aggregateLimit: z.string().trim(),
  occurrenceLimit: z.string().trim(),
  premium: z.string().trim(),
  aggregate: z.string().trim(),
  occurrence: z.string().trim(),
  amBest: z.string().trim(),
});

type DisplayOptionSchemaType = z.infer<typeof DisplayOptionsSchema>;
</script>

<script setup lang="ts">
import {inject, computed, ref, onBeforeMount, onUnmounted, watch} from 'vue';
import {Store} from 'vuex';
import {AppState} from '@/store';
import {StoreMutationType} from '@/types/StoreMutationType';
import AppModal from '@/components/v2/AppModal.vue';
import AppButton from '@/components/v2/AppButton.vue';
import TextField from '@/components/v2/TextField.vue';

const store = inject<Store<AppState>>('store');

const props = defineProps<{
  show: boolean;
}>();

const showDialog = ref(false);

let displayOptions = store?.state.towerCanvasState.displayOptions;

const form = ref<DisplayOptionSchemaType>({
  carrier: store?.state.towerCanvasState.displayOptions.carrier?.customName || '',
  aggregateLimit: store?.state.towerCanvasState.displayOptions.aggregateLimit?.customName || '',
  occurrenceLimit: store?.state.towerCanvasState.displayOptions.occurrenceLimit?.customName || '',
  premium: store?.state.towerCanvasState.displayOptions.premium?.customName || '',
  aggregate: store?.state.towerCanvasState.displayOptions.aggregate?.customName || '',
  occurrence: store?.state.towerCanvasState.displayOptions.occurrence?.customName || '',
  amBest: store?.state.towerCanvasState.displayOptions.amBest?.customName || '',
});

// ------------
// lifecycles
// ------------
onBeforeMount(async () => {
  addEventListener('keyup', handleKeyUp);
  showDialog.value = props.show;
});

onUnmounted(() => {
  removeEventListener('keyup', handleKeyUp);
});

// ---------------------
// computed properties
// ---------------------

const displayOptionsState = computed(() => store?.state.towerCanvasState.displayOptions);

const carrier = computed({
  get: () => {
    return form.value.carrier;
  },
  set: (val) => {
    form.value.carrier = val;
  },
});

const aggregateLimit = computed({
  get: () => {
    return form.value.aggregateLimit;
  },
  set: (val) => {
    form.value.aggregateLimit = val;
  },
});


const occurrenceLimit = computed({
  get: () => {
    return form.value.occurrenceLimit;
  },
  set: (val) => {
    form.value.occurrenceLimit = val;
  },
});

const premium = computed({
  get: () => {
    return form.value.premium;
  },
  set: (val) => {
    form.value.premium = val;
  },
});

const aggregate = computed({
  get: () => {
    return form.value.aggregate;
  },
  set: (val) => {
    form.value.aggregate = val;
  },
});

const occurrence = computed({
  get: () => {
    return form.value.occurrence;
  },
  set: (val) => {
    form.value.occurrence = val;
  },
});

const amBest = computed({
  get: () => {
    return form.value.amBest;
  },
  set: (val) => {
    form.value.amBest = val;
  },
});

// ---------------------
// watch
// ---------------------
watch(displayOptionsState, (val) => {
  displayOptions = val;
});

// -------------------
// private functions
// -------------------
function closeDialog(): void {
  store?.commit(StoreMutationType.EDIT_TOWER_DISPLAY_OPTIONS, {
    show: false,
  });
}

function updateDisplayOptions(): void {
  if (!displayOptions) return;
  
  if (displayOptions.carrier) displayOptions.carrier.customName = form.value.carrier;
  if (displayOptions.aggregateLimit) displayOptions.aggregateLimit.customName = form.value.aggregateLimit;
  if (displayOptions.occurrenceLimit) displayOptions.occurrenceLimit.customName = form.value.occurrenceLimit;
  if (displayOptions.premium) displayOptions.premium.customName = form.value.premium;
  if (displayOptions.aggregate) displayOptions.aggregate.customName = form.value.aggregate;
  if (displayOptions.occurrence) displayOptions.occurrence.customName = form.value.occurrence;
  if (displayOptions.amBest) displayOptions.amBest.customName = form.value.amBest;
  
  store?.commit(StoreMutationType.SET_TOWER_DISPLAY_OPTIONS, displayOptions);
}

// ----------
// handlers
// ----------
function handleKeyUp(event: KeyboardEvent): void {
  if (event.key === 'Escape') {
    closeDialog();
  }
}

function handleSave(): void {
  try {
    updateDisplayOptions();
    // TODO: Update display opts here
    closeDialog();
  } catch (e) {
    console.error('an error occurred when trying to save: %o', e);
  }
}

</script>

<template>
  <AppModal
    :show="show"
    title="Edit Display Option Labels"
    cancel-button-label="Cancel"
    action-button-label="Save"
    :handle-cancel="closeDialog"
    :handle-action="handleSave"
    size="lg"
    data-test="edit-display-options-modal"
  >
    <template #content>
      <p class="tw:text-xs tw:italic tw:font-normal tw:pb-2 tw:mb-4">Custom labels will be displayed on the Legend</p>
      <div class="tw:space-y-4" style="min-height: 280px">
        <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4">
          <TextField label="Carrier" v-model="carrier" />
          <TextField label="Aggregate Limit" v-model="aggregateLimit" />
        </div>
        <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4">
          <TextField label="Occurrence Limit" v-model="occurrenceLimit" />
          <TextField label="Premium" v-model="premium" />
        </div>
        <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4">
          <TextField label="Rate on Line (Aggregate)" v-model="aggregate" />
          <TextField label="Rate on Line (Occurrence)" v-model="occurrence" />
        </div>
        <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4">
          <TextField label="AM Best" v-model="amBest" />
          <div />
        </div>
      </div>
    </template>
    <template #actions>
      <AppButton @click="closeDialog" data-test="cancel-action">Cancel</AppButton>
      <AppButton primary @click="handleSave" data-test="confirm-action">Save</AppButton>
    </template>
  </AppModal>
</template>
